import React, { useState, useCallback, useRef } from 'react';
import { Upload, FileText, X, CheckCircle, Loader, AlertCircle, FolderOpen } from 'lucide-react';
import { useBulkUploadDocumentsVendorBasedMutation, useGetVendorsQuery } from '../../store/api/apiSlice';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';

interface BulkDocumentUploadProps {
  vendorId?: string;
  onUploadComplete?: (documents: any[]) => void;
  onClose?: () => void;
}

interface FileWithStatus {
  file: File;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

export const BulkDocumentUpload: React.FC<BulkDocumentUploadProps> = ({
  vendorId,
  onUploadComplete,
  onClose
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileWithStatus[]>([]);
  const [selectedVendorId, setSelectedVendorId] = useState(vendorId || '');
  const [documentType, setDocumentType] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [bulkUploadDocuments] = useBulkUploadDocumentsVendorBasedMutation();
  const auth = useSelector((state: any) => state.auth);
  const isAuthReady = auth.isAuthenticated && !auth.isLoading && auth.token;
  const { data: vendors, isLoading: isLoadingVendors } = useGetVendorsQuery({ skip: 0, limit: 100 }, {
    // Skip API call until authentication is ready
    skip: !isAuthReady
  });

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    addFiles(files);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    addFiles(files);
  };

  const addFiles = (files: File[]) => {
    const validFiles = files.filter(file => {
      // Check file type
      const validTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!validTypes.includes(file.type)) {
        toast.error(`${file.name}: Unsupported file type. Please upload PDF, DOCX, or TXT files.`);
        return false;
      }
      
      // Check file size (10MB limit)
      const maxSize = 10 * 1024 * 1024;
      if (file.size > maxSize) {
        toast.error(`${file.name}: File too large. Maximum size is 10MB.`);
        return false;
      }
      
      return true;
    });

    const newFiles: FileWithStatus[] = validFiles.map(file => ({
      file,
      status: 'pending' as const
    }));

    setSelectedFiles(prev => [...prev, ...newFiles]);
  };

  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const removeAllFiles = () => {
    setSelectedFiles([]);
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) {
      toast.error('Please select files to upload');
      return;
    }

    // Check if user is authenticated
    if (!auth.isAuthenticated || !auth.token) {
      toast.error('You must be logged in to upload documents');
      return;
    }

    setIsUploading(true);

    try {
      const formData = new FormData();
      
      // Add files to form data
      selectedFiles.forEach(({ file }) => {
        formData.append('files', file);
      });
      
      // Add vendor (if selected) and document type
      if (selectedVendorId) {
        formData.append('vendor_id', selectedVendorId);
      }
      if (documentType) {
        formData.append('document_type', documentType);
      }

      const result = await bulkUploadDocuments(formData).unwrap();
      
      toast.success(`Successfully uploaded ${result.successful_uploads} documents!`);
      
      if (onUploadComplete) {
        onUploadComplete(result.documents);
      }
      
      if (onClose) {
        onClose();
      }
      
    } catch (error: any) {
      console.error('Bulk upload error:', error);

      // Handle quota exceeded error specifically
      if (error?.data?.detail?.code === 'DOCUMENT_QUOTA_EXCEEDED') {
        const detail = error.data.detail;
        toast.error(
          `Document quota exceeded. You have ${detail.available} of ${detail.quota} documents remaining.`,
          { duration: 6000 }
        );
      } else {
        toast.error(
          error?.data?.detail?.message || error?.data?.detail || 'Bulk upload failed'
        );
      }
    } finally {
      setIsUploading(false);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const totalSize = selectedFiles.reduce((sum, { file }) => sum + file.size, 0);
  const totalFiles = selectedFiles.length;

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            Bulk Upload Documents
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Upload multiple documents at once for efficient processing
          </p>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        )}
      </div>

      <div className="space-y-6">
        {/* Vendor Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Vendor (Optional)
          </label>
          <select
            value={selectedVendorId}
            onChange={(e) => setSelectedVendorId(e.target.value)}
            disabled={isLoadingVendors}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50"
          >
            <option value="">
              {isLoadingVendors ? 'Loading vendors...' : ''}
            </option>
            {vendors?.items.map((vendor) => (
              <option key={vendor.id} value={vendor.id}>
                {vendor.name}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            If no vendor is selected, AI will automatically detect and assign vendors from contract content
          </p>
        </div>

        {/* Document Type */}
        {/*<div>*/}
        {/*  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">*/}
        {/*    Document Type (Optional)*/}
        {/*  </label>*/}
        {/*  <select*/}
        {/*    value={documentType}*/}
        {/*    onChange={(e) => setDocumentType(e.target.value)}*/}
        {/*    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"*/}
        {/*  >*/}
        {/*    <option value="">Select type</option>*/}
        {/*    <option value="contract">Contract</option>*/}
        {/*    <option value="agreement">Agreement</option>*/}
        {/*    <option value="memo">Memo</option>*/}
        {/*    <option value="policy">Policy</option>*/}
        {/*    <option value="other">Other</option>*/}
        {/*  </select>*/}
        {/*</div>*/}

        {/* File Drop Zone */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragOver
              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/10'
              : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="space-y-4">
            <FolderOpen className="w-16 h-16 text-gray-400 mx-auto" />
            <div>
              <p className="text-xl font-medium text-gray-900 dark:text-gray-100">
                Drop multiple documents here
              </p>
              <p className="text-gray-500 dark:text-gray-400 mt-2">
                or{' '}
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                >
                  browse files
                </button>
              </p>
              <p className="text-sm text-gray-400 mt-4">
                Supports PDF, DOCX, TXT files up to 10MB each
              </p>
            </div>
          </div>
          
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept=".pdf,.docx,.txt"
            multiple
            className="hidden"
          />
        </div>

        {/* Selected Files List */}
        {selectedFiles.length > 0 && (
          <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <FileText className="w-5 h-5 text-gray-500" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100">
                  Selected Files ({totalFiles})
                </h3>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Total: {formatFileSize(totalSize)}
                </span>
              </div>
              <button
                onClick={removeAllFiles}
                className="text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 text-sm font-medium"
              >
                Remove All
              </button>
            </div>
            
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {selectedFiles.map(({ file, status, error }, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700"
                >
                  <div className="flex items-center space-x-3">
                    <FileText className="w-5 h-5 text-gray-400" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {file.name}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatFileSize(file.size)}
                      </p>
                      {error && (
                        <p className="text-xs text-red-500 mt-1">{error}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {status === 'pending' && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">Ready</span>
                    )}
                    {status === 'uploading' && (
                      <Loader className="w-4 h-4 animate-spin text-blue-500" />
                    )}
                    {status === 'success' && (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    )}
                    {status === 'error' && (
                      <AlertCircle className="w-4 h-4 text-red-500" />
                    )}
                    <button
                      onClick={() => removeFile(index)}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                    >
                      <X className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Upload Button */}
        <div className="flex justify-end space-x-3 pt-6">
          {onClose && (
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleUpload}
            disabled={!selectedFiles.length || isUploading}
            className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading ? (
              <>
                <Loader className="w-4 h-4 mr-2 animate-spin" />
                Uploading {totalFiles} files...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload {totalFiles} Documents
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}; 